import { getExecutingEnvironment } from "@app/resolveModule/env";
import resolveModule from "@app/resolveModule/frontend";
import FetchService from "@core-ui/ApiServices/FetchService";
import Method from "@core-ui/ApiServices/Types/Method";
import MimeTypes from "@core-ui/ApiServices/Types/MimeTypes";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ArticlePropsService from "@core-ui/ContextServices/ArticleProps";
import fileNameUtils from "@core-ui/fileNameUtils";
import { isExternalLink } from "@core-ui/hooks/useExternalLink";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import Path from "@core/FileProvider/Path/Path";
import { ArticleProviderType } from "@ext/articleProvider/logic/ArticleProvider";
import getArticleFileBrotherNames from "@ext/markdown/elementsUtils/AtricleResource/getAtricleResourceNames";
import { ReactElement, createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

type UseGetResource = (
	callback: (buffer: Buffer) => void | Promise<void>,
	src: string,
	content?: string,
	haveParentPath?: boolean,
	isPrint?: boolean,
) => void;

type SetResource = (name: string, file: string | Buffer, path?: string, force?: boolean) => Promise<string>;

type DeleteResource = (src: string) => Promise<void>;

type ResourceData = Record<string, Buffer>;

export type ResourceServiceType = {
	data: ResourceData;
	id?: string;
	provider?: ArticleProviderType;
	useGetResource: UseGetResource;
	getResource: (src: string) => Promise<Buffer>;
	setResource: SetResource;
	deleteResource: DeleteResource;
	getBuffer: (src: string) => Buffer;
	clear: () => void;
	update: (src: string, buffer: Buffer) => void;
};

const ResourceServiceContext = createContext<ResourceServiceType>({
	data: {},
	id: undefined,
	provider: undefined,
	useGetResource: () => {},
	getResource: async () => Promise.resolve(Buffer.from("")),
	deleteResource: async () => {},
	setResource: async () => Promise.resolve(""),
	getBuffer: () => Buffer.from(""),
	clear: () => {},
	update: () => {},
});

type ResourceServiceProps = {
	children: ReactElement;
	id?: string;
	provider?: ArticleProviderType;
};

abstract class ResourceService {
	private static _loadingPromises: Set<Promise<void>> = new Set();

	static Provider({ children, provider, id }: ResourceServiceProps) {
		const [data, setData] = useState<ResourceData>({});
		const catalogName = useCatalogPropsStore((state) => state.data?.name);
		const articleProps = ArticlePropsService.value;
		const apiUrlCreator = ApiUrlCreatorService.value;

		const clear = useCallback(() => {
			setData({});
		}, [setData]);

		useEffect(() => {
			if (!id) return;
			clear();
		}, [id]);

		const update = useCallback(
			(src: string, buffer: Buffer) => {
				setData((prevData) => ({ ...prevData, [src]: buffer }));
			},
			[data, setData],
		);

		const getResource = useCallback(
			async (src: string) => {
				if (data?.[src]) return data?.[src];
				const url = apiUrlCreator.getArticleResource(src, undefined, catalogName, id, provider);

				const res = await FetchService.fetch(url, undefined, MimeTypes.text, Method.POST, false);

				if (!res.ok) return;
				return await res.buffer();
			},
			[data, apiUrlCreator, catalogName, id, provider],
		);

		const getNoParentResource = useCallback(
			async (fullResourcePath: Path) => {
				const url = apiUrlCreator.getResourceByPath(fullResourcePath.value);
				const res = await FetchService.fetch(url, undefined, MimeTypes.text, Method.POST, false);
				if (!res.ok) return;

				return await res.buffer();
			},
			[apiUrlCreator],
		);

		const useGetResource: UseGetResource = (callback, src, content?, haveParentPath = true, isPrint?) => {
			const resolvePromiseRef = useRef<(() => void) | null>(null);
			const promiseRef = useRef<Promise<void> | null>(null);

			useEffect(() => {
				if (!isPrint) return;
				promiseRef.current = new Promise<void>((resolve) => {
					resolvePromiseRef.current = resolve;
				});
				ResourceService._loadingPromises.add(promiseRef.current);
				promiseRef.current.finally(() => ResourceService._loadingPromises.delete(promiseRef.current));

				return () => {
					resolvePromiseRef.current();
				};
			}, []);

			const fetchImage = async (src: string) => {
				const res = await fetch(src);
				if (!res.ok) return;

				const blob = await res.blob();
				return new Uint8Array(await blob.arrayBuffer());
			};

			const fetchInTauri = async (src) => {
				const res = await resolveModule("httpFetch")({ url: src });
				if (!res?.body || res.body.type !== "binary") return;
				return res.body.data;
			};

			const loadExternalData = async (src: string) => {
				const buffer = await (getExecutingEnvironment() === "tauri" ? fetchInTauri(src) : fetchImage(src));
				if (buffer) return Buffer.from(buffer);
			};

			const loadInternalData = async (src: string) => {
				const url = apiUrlCreator.getArticleResource(src, undefined, catalogName, id, provider);

				const res = await FetchService.fetch(url, undefined, MimeTypes.text, Method.POST, false);

				if (!res.ok) return;
				return await res.buffer();
			};

			const wrappedCallback = useCallback<typeof callback>(
				async (buffer) => {
					try {
						await Promise.resolve(callback(buffer));
					} finally {
						resolvePromiseRef.current?.();
					}
				},
				[callback, isPrint],
			);

			const loadData = async (src: string) => {
				let buffer: Buffer;
				try {
					if (!haveParentPath) {
						buffer = await getNoParentResource(new Path(src));
					} else {
						buffer = isExternalLink(src).isUrl ? await loadExternalData(src) : await loadInternalData(src);
					}
				} catch (e) {
					if (isPrint) wrappedCallback(undefined);
					throw e;
				}

				if (!buffer || !buffer.length) return wrappedCallback(undefined);
				update(src, buffer);
			};

			useEffect(() => {
				if (content) {
					wrappedCallback(Buffer.from(content));
					return;
				}

				if (data?.[src]) {
					wrappedCallback(data[src]);
					return;
				}

				loadData(src);
			}, [src, data?.[src], content]);
		};

		const setResource: SetResource = useCallback(
			async (name, file, path, force) => {
				const pathedName = new Path(name);
				const extension = pathedName.extension?.toLowerCase();

				const names = force ? [] : await getArticleFileBrotherNames(apiUrlCreator, id, provider);
				const newName = fileNameUtils.getNewName(names, pathedName.name ?? articleProps.fileName, extension);

				const fullResourcePath = new Path([path, newName]);
				const url = apiUrlCreator.setArticleResource(fullResourcePath.value, id, provider);

				const res = await FetchService.fetch(url, file, MimeTypes.text);
				if (!res.ok) return;

				update(newName, typeof file == "string" ? Buffer.from(file) : file);

				names.push(newName);

				return newName;
			},
			[setData, apiUrlCreator, articleProps?.fileName, update, provider, id],
		);

		const deleteResource: DeleteResource = useCallback(
			async (src: string) => {
				const url = apiUrlCreator.deleteArticleResource(src, id, provider);
				await FetchService.fetch(url);
			},
			[setData, apiUrlCreator, provider, id],
		);

		const getBuffer = useCallback(
			(src: string): Buffer => {
				return data?.[src];
			},
			[data],
		);

		return (
			<ResourceServiceContext.Provider
				value={{
					data,
					id,
					provider,
					useGetResource,
					getResource,
					setResource,
					deleteResource,
					getBuffer,
					clear,
					update,
				}}
			>
				{children}
			</ResourceServiceContext.Provider>
		);
	}

	static get value(): ResourceServiceType {
		return useContext(ResourceServiceContext);
	}

	static async waitForAllLoads(signal?: AbortSignal) {
		const yieldThread = () =>
			new Promise<void>((resolve) => {
				setTimeout(resolve, 0);
			});

		while (ResourceService._loadingPromises.size) {
			if (signal?.aborted) return;
			const pending = Array.from(ResourceService._loadingPromises);
			await Promise.race([Promise.allSettled(pending), yieldThread()]);
		}
	}
}

export default ResourceService;
